import React from "react";
import {toast} from "react-toastify";

export const formatQuizStatusAsPill = (status) => {
    const pillStyle = {
        padding: '4px 12px',
        borderRadius: '16px',
        fontSize: '14px',
        fontWeight: '500',
        display: 'inline-block'
    };
    switch (status) {
        case 'active':
            return <span style={{ ...pillStyle, backgroundColor: '#ECFDF3', color: '#027A48' }}>Aktivan</span>;
        case 'inactive':
            return <span style={{ ...pillStyle, backgroundColor: '#EFF8FF', color: '#175CD3' }}>Neaktivan</span>;
        default:
            return <span style={{ ...pillStyle, backgroundColor: '#F2F4F7', color: '#344054' }}>Nepoznat status</span>;
    }
};

export const QUIZ_FILTER_OPTIONS = [
    { label: 'Aktivan', value: 'active' },
    { label: 'Neaktivan', value: 'inactive' },
];

export const QUIZ_STATUS_OPTIONS = [
    { value: 'active', label: 'Aktivan' },
    { value: 'inactive', label: 'Neaktivan' },
];

export const QUESTION_TYPE_OPTIONS = [
    { value: 'single-choice', label: 'Jedan odgovor (radio dugmad)' },
    { value: 'multiple-choice', label: 'Više odgovora (checkbox)' },
    { value: 'written-text', label: 'Slobodan tekstualni odgovor' },
];

export const WRITTEN_TEXT_QUESTION_TYPE = 'written-text';

// Flat, fixed bonus the backend awards once per quiz containing a written-text
// question (not per-question, not configurable) - shown here only as an
// informational label, never sent to the API.
export const WRITTEN_QUIZ_BONUS_POINTS = 10;

const emptyMultilingual = () => ({ sr: '', hu: '', en: '' });

export const emptyQuizOption = () => ({
    text: emptyMultilingual(),
    isCorrect: false,
});

export const emptyQuizQuestion = () => ({
    text: emptyMultilingual(),
    type: 'single-choice',
    options: [emptyQuizOption(), emptyQuizOption()],
});

export const emptyQuizForm = () => ({
    _id: '',
    name: '',
    status: 'active',
    title: emptyMultilingual(),
    description: {
        sr: undefined,
        hu: undefined,
        en: undefined,
    },
    showResults: true,
    displayInPage: '',
    startDate: undefined,
    endDate: undefined,
    displayFrequency: undefined,
    targetUserGroup: '',
    questions: [emptyQuizQuestion()],
    minCorrectPercentToEarnPoints: undefined,
    pointsPerCorrectAnswer: undefined,
});

// A question is "scored" if it's a choice question with at least one correct
// option - mirrors QuizService.quizHasScoredChoiceQuestion on the backend.
// The two scoring fields (min % + points/correct) are required only once a
// quiz has at least one such question.
export const quizHasScoredChoiceQuestion = (questions) => {
    return (questions || []).some(
        (q) =>
            q.type !== WRITTEN_TEXT_QUESTION_TYPE &&
            (q.options || []).some((o) => o.isCorrect),
    );
};

// When a single-choice question's option is marked correct, every other
// option in that same question must be un-marked - only one can be right.
export const applySingleChoiceExclusivity = (question, correctOptionIndex) => {
    return {
        ...question,
        options: question.options.map((opt, idx) => ({
            ...opt,
            isCorrect: idx === correctOptionIndex,
        })),
    };
};

const validateMultilingualRequired = (data, fieldLabel) => {
    if (!data || !data.sr?.trim() || !data.hu?.trim() || !data.en?.trim()) {
        toast.error(`${fieldLabel} mora biti popunjen na svim jezicima.`);
        return false;
    }
    return true;
};

const validateMultilingualOptional = (data, fieldLabel) => {
    if (!data) return true;
    const { sr, hu, en } = data;
    const isAnyPopulated = (sr && sr.trim() !== '') || (hu && hu.trim() !== '') || (en && en.trim() !== '');
    if (isAnyPopulated) {
        if (!sr?.trim() || !hu?.trim() || !en?.trim()) {
            toast.error(`${fieldLabel} ne sme biti delimično popunjen - popunite ga na svim jezicima ili ga ostavite prazan.`);
            return false;
        }
    }
    return true;
};

export const validatedQuizBasics = (formData) => {
    let error = false;

    if (!formData.name || formData.name.trim() === '') {
        toast.error('Interni naziv ne sme biti prazan.');
        error = true;
    }
    if (!validateMultilingualRequired(formData.title, 'Naslov kviza')) {
        error = true;
    }
    if (!validateMultilingualOptional(formData.description, 'Opis kviza')) {
        error = true;
    }
    return !error;
};

export const validatedQuizQuestions = (formData) => {
    let error = false;

    if (!formData.questions || formData.questions.length === 0) {
        toast.error('Kviz mora imati bar jedno pitanje.');
        return false;
    }

    formData.questions.forEach((question, qIndex) => {
        if (!validateMultilingualRequired(question.text, `Tekst pitanja ${qIndex + 1}`)) {
            error = true;
        }

        if (question.type === WRITTEN_TEXT_QUESTION_TYPE) {
            return; // no options to validate - free-text questions carry no answer options
        }

        if (!question.options || question.options.length < 2) {
            toast.error(`Pitanje ${qIndex + 1} mora imati bar 2 ponuđena odgovora.`);
            error = true;
        } else {
            question.options.forEach((option, oIndex) => {
                if (!validateMultilingualRequired(option.text, `Odgovor ${oIndex + 1} (pitanje ${qIndex + 1})`)) {
                    error = true;
                }
            });
            const correctCount = question.options.filter((o) => o.isCorrect).length;
            if (question.type === 'single-choice' && correctCount > 1) {
                toast.error(`Pitanje ${qIndex + 1}: samo jedan odgovor može biti tačan kod pitanja sa jednim odgovorom.`);
                error = true;
            }
        }
    });

    if (quizHasScoredChoiceQuestion(formData.questions)) {
        const { minCorrectPercentToEarnPoints, pointsPerCorrectAnswer } = formData;
        if (
            minCorrectPercentToEarnPoints === undefined ||
            minCorrectPercentToEarnPoints === null ||
            minCorrectPercentToEarnPoints === '' ||
            minCorrectPercentToEarnPoints < 0 ||
            minCorrectPercentToEarnPoints > 100
        ) {
            toast.error('Minimalan procenat tačnih odgovora za osvajanje poena mora biti broj između 0 i 100 (kviz sadrži bodovano pitanje sa tačnim odgovorom).');
            error = true;
        }
        if (
            pointsPerCorrectAnswer === undefined ||
            pointsPerCorrectAnswer === null ||
            pointsPerCorrectAnswer === '' ||
            pointsPerCorrectAnswer <= 0
        ) {
            toast.error('Broj poena po tačnom odgovoru mora biti veći od 0 (kviz sadrži bodovano pitanje sa tačnim odgovorom).');
            error = true;
        }
    }

    return !error;
};

export const validatedQuizTrigger = (formData) => {
    let error = false;
    const isEmpty = (val) => val === undefined || val === null || val === '';

    if (isEmpty(formData.displayInPage)) {
        toast.error('Izabrana stranica ne sme biti prazna.');
        error = true;
    }
    if (!formData.startDate) {
        toast.error('Datum starta ne sme biti prazan.');
        error = true;
    }
    if (!formData.endDate) {
        toast.error('Datum kraja ne sme biti prazan.');
        error = true;
    }
    if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        if (start > end) {
            toast.error('Datum kraja mora biti nakon datuma starta.');
            error = true;
        }
    }
    if (!formData.displayFrequency) {
        toast.error('Frekvencija prikaza ne sme biti prazna.');
        error = true;
    }
    if (isEmpty(formData.targetUserGroup)) {
        toast.error('Grupa korisnika ne sme biti prazna.');
        error = true;
    }
    return !error;
};

export const validatedQuizForm = (formData) => {
    if (!validatedQuizBasics(formData)) return false;
    if (!validatedQuizQuestions(formData)) return false;
    if (!validatedQuizTrigger(formData)) return false;
    return true;
};
